<template>
  <div class="chat-dock">
    <transition name="chat-pop">
      <div v-if="open" class="chat-panel">
        <div ref="history" class="chat-history">
          <div v-if="!messages.length" class="chat-empty">暂无消息</div>
          <div
            v-for="msg in messages"
            :key="msg.id"
            class="chat-line"
            :class="{ mine: msg.playerId === selfId }"
          >
            <div class="chat-meta">
              <span class="chat-name">{{ msg.playerName }}</span>
              <span class="chat-time">{{ formatTime(msg.timestamp) }}</span>
            </div>
            <div class="chat-text">
              <ChatRichText :text="msg.text" />
            </div>
          </div>
        </div>
        <div v-if="emojiOpen" ref="emojiPanel" class="emoji-panel" @mousedown.prevent @click.stop>
          <button
            v-for="item in emojis"
            :key="item.id"
            type="button"
            class="emoji-item"
            @click="insertEmoji(item)"
          >
            <img :src="item.src" alt="表情" draggable="false" />
          </button>
        </div>
        <div class="chat-input-row">
          <div
            ref="input"
            class="chat-editor"
            :class="{ 'is-empty': editorEmpty }"
            contenteditable="true"
            spellcheck="false"
            role="textbox"
            aria-label="输入消息"
            data-placeholder="输入消息"
            @input="onEditorInput"
            @keydown="onKeydown"
            @paste="onPaste"
            @compositionstart="onCompositionStart"
            @compositionend="onCompositionEnd"
            @blur="saveRange"
            @keyup="saveRange"
            @mouseup="saveRange"
            @focus="saveRange"
          ></div>
          <button
            type="button"
            class="emoji-btn"
            :class="{ active: emojiOpen }"
            aria-label="表情"
            @mousedown.prevent
            @click.stop="toggleEmoji"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6" />
              <circle cx="9" cy="10" r="1" fill="currentColor" />
              <circle cx="15" cy="10" r="1" fill="currentColor" />
              <path d="M8.5 14.2c.8 1.3 2 2 3.5 2s2.7-.7 3.5-2" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
            </svg>
          </button>
          <button type="button" class="send-btn" :disabled="!canSend" @click="onSend">发送</button>
        </div>
      </div>
    </transition>

    <button type="button" class="chat-toggle" aria-label="聊天" @click="$emit('toggle')">
      <svg class="icon" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M535.272727 593.454545h-209.454545c-25.6 0-46.545455 20.945455-46.545455 46.545455s20.945455 46.545455 46.545455 46.545455h209.454545c25.6 0 46.545455-20.945455 46.545455-46.545455s-20.945455-46.545455-46.545455-46.545455zM698.181818 384H325.818182c-25.6 0-46.545455 20.945455-46.545455 46.545455s20.945455 46.545455 46.545455 46.545454h372.363636c25.6 0 46.545455-20.945455 46.545455-46.545454s-20.945455-46.545455-46.545455-46.545455z" fill="#ffffff"></path>
        <path d="M512 0C230.4 0 0 230.4 0 512v302.545455c0 116.363636 93.090909 209.454545 209.454545 209.454545h302.545455c281.6 0 512-230.4 512-512S793.6 0 512 0z m0 930.909091H209.454545c-65.163636 0-116.363636-51.2-116.363636-116.363636V512c0-230.4 188.509091-418.909091 418.909091-418.909091s418.909091 188.509091 418.909091 418.909091-188.509091 418.909091-418.909091 418.909091z" fill="#ffffff"></path>
      </svg>
    </button>
  </div>
</template>

<script>
import ChatRichText from '@/components/ChatRichText.vue'
import { chatEmojis, parseChatText } from '@/utils/chatEmoji'

const MAX_LEN = 100

export default {
  name: 'ChatPanel',
  components: { ChatRichText },
  props: {
    value: {
      type: String,
      default: ''
    },
    open: {
      type: Boolean,
      default: false
    },
    messages: {
      type: Array,
      default: () => []
    },
    selfId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      emojis: chatEmojis,
      emojiOpen: false,
      editorEmpty: true,
      composing: false
    }
  },
  computed: {
    canSend() {
      return !!(this.value && this.value.trim())
    }
  },
  watch: {
    open(val) {
      if (!val) {
        this.emojiOpen = false
        return
      }
      this.$nextTick(() => {
        this.syncEditor()
        this.scrollToBottom()
        if (this.$refs.input) this.$refs.input.focus()
      })
    },
    value() {
      if (!this.open) return
      this.$nextTick(() => this.syncEditor())
    },
    messages() {
      if (!this.open) return
      this.$nextTick(this.scrollToBottom)
    }
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('selectionchange', this.saveRange)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('selectionchange', this.saveRange)
  },
  methods: {
    onSend() {
      if (!this.canSend) return
      this.emojiOpen = false
      this.$emit('send')
    },
    toggleEmoji() {
      this.emojiOpen = !this.emojiOpen
    },
    onDocumentClick(event) {
      if (!this.emojiOpen) return
      const panel = this.$refs.emojiPanel
      if (panel && panel.contains(event.target)) return
      this.emojiOpen = false
    },
    onEditorInput() {
      if (this.composing) {
        this.refreshEmpty()
        return
      }
      this.commitEditor()
    },
    refreshEmpty() {
      const editor = this.$refs.input
      this.editorEmpty = !editor || !this.serialize(editor)
    },
    onCompositionStart() {
      this.composing = true
      this.editorEmpty = false
    },
    onCompositionEnd() {
      this.composing = false
      this.commitEditor()
    },
    onKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault()
        this.onSend()
        return
      }
      if (event.key === 'Backspace' || event.key === 'Delete' || event.ctrlKey || event.metaKey || event.altKey) return
      if (event.key.length !== 1) return
      const editor = this.$refs.input
      const selected = this.rangeInEditor()
      const selectedText = selected && !selected.collapsed ? selected.toString() : ''
      if (editor && this.serialize(editor).length - selectedText.length >= MAX_LEN) {
        event.preventDefault()
      }
    },
    onPaste(event) {
      event.preventDefault()
      const clipboard = event.clipboardData || window.clipboardData
      const raw = clipboard ? clipboard.getData('text/plain') : ''
      const text = String(raw || '').replace(/[\r\n]+/g, '')
      if (!text) return
      this.insertText(text)
    },
    insertEmoji(item) {
      const editor = this.$refs.input
      if (!editor || !item) return
      if (this.serialize(editor).length + item.token.length > MAX_LEN) return
      const range = this.rangeForInsert()
      editor.focus()
      range.deleteContents()
      const image = this.createEmojiNode(item)
      range.insertNode(image)
      range.setStartAfter(image)
      range.collapse(true)
      this.applyRange(range)
      this.commitEditor()
    },
    insertText(text) {
      const editor = this.$refs.input
      if (!editor) return
      const range = this.rangeForInsert()
      editor.focus()
      range.deleteContents()
      const node = document.createTextNode(text)
      range.insertNode(node)
      range.setStartAfter(node)
      range.collapse(true)
      this.applyRange(range)
      this.commitEditor()
    },
    commitEditor() {
      const editor = this.$refs.input
      if (!editor) return
      editor.querySelectorAll('br').forEach(node => node.remove())
      const text = this.serialize(editor)
      if (!text) {
        if (editor.childNodes.length) editor.textContent = ''
        this.editorEmpty = true
        this._savedRange = null
        if (this.value) this.$emit('input', '')
        return
      }
      if (text.length > MAX_LEN) {
        this.renderValue(this.value || '')
        return
      }
      this.editorEmpty = false
      if (text !== this.value) this.$emit('input', text)
    },
    syncEditor() {
      const editor = this.$refs.input
      if (!editor || this.composing) return
      const next = this.value || ''
      if (this.serialize(editor) === next) {
        if (!next && editor.childNodes.length) editor.textContent = ''
        this.editorEmpty = !next
        return
      }
      this.renderValue(next)
    },
    renderValue(text) {
      const editor = this.$refs.input
      if (!editor) return
      const focused = document.activeElement === editor
      editor.textContent = ''
      const fragment = document.createDocumentFragment()
      parseChatText(text).forEach(part => {
        if (part.type === 'emoji') {
          fragment.appendChild(this.createEmojiNode(part))
        } else if (part.text) {
          fragment.appendChild(document.createTextNode(part.text))
        }
      })
      editor.appendChild(fragment)
      this.editorEmpty = !text
      if (focused) this.placeCaretAtEnd()
    },
    createEmojiNode(item) {
      const image = document.createElement('img')
      image.src = item.src
      image.alt = '表情'
      image.draggable = false
      image.dataset.emoji = String(item.id)
      image.className = 'chat-inline-emoji'
      image.setAttribute('contenteditable', 'false')
      return image
    },
    serialize(editor) {
      let text = ''
      const walk = (node) => {
        node.childNodes.forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) {
            text += child.textContent.replace(/\u00a0/g, ' ')
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const id = child.dataset && child.dataset.emoji
            if (id) text += `[e${id}]`
            else if (child.tagName !== 'BR') walk(child)
          }
        })
      }
      walk(editor)
      return text.replace(/[\r\n]/g, '')
    },
    rangeInEditor() {
      const editor = this.$refs.input
      const selection = window.getSelection()
      if (!editor || !selection || !selection.rangeCount) return null
      const range = selection.getRangeAt(0)
      if (!editor.contains(range.startContainer)) return null
      return range
    },
    rangeForInsert() {
      const editor = this.$refs.input
      const live = this.rangeInEditor()
      if (live) return live.cloneRange()
      const saved = this._savedRange
      if (saved && editor && editor.contains(saved.startContainer)) return saved.cloneRange()
      const range = document.createRange()
      range.selectNodeContents(editor)
      range.collapse(false)
      return range
    },
    applyRange(range) {
      const selection = window.getSelection()
      if (!selection) return
      selection.removeAllRanges()
      selection.addRange(range)
      this._savedRange = range.cloneRange()
    },
    saveRange() {
      const range = this.rangeInEditor()
      if (range) this._savedRange = range.cloneRange()
    },
    placeCaretAtEnd() {
      const editor = this.$refs.input
      if (!editor) return
      const range = document.createRange()
      range.selectNodeContents(editor)
      range.collapse(false)
      this.applyRange(range)
    },
    scrollToBottom() {
      const el = this.$refs.history
      if (el) el.scrollTop = el.scrollHeight
    },
    formatTime(timestamp) {
      const time = Number(timestamp)
      if (!time) return ''
      const date = new Date(time)
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-dock {
  position: fixed;
  left: 12px;
  bottom: calc(12px + env(safe-area-inset-bottom));
  z-index: 9000;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.chat-panel {
  position: relative;
  width: min(300px, calc(100vw - 24px));
  height: min(320px, 52vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(248, 239, 148, 0.75);
  border-radius: 12px;
  background: rgba(20, 40, 28, 0.96);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  transform-origin: left bottom;
}

.chat-history {
  flex: 1;
  overflow-y: auto;
  padding: 10px 12px;
  -webkit-overflow-scrolling: touch;
}

.chat-empty {
  color: rgba(255, 244, 184, 0.55);
  font-size: 13px;
  text-align: center;
  padding-top: 24px;
}

.chat-line {
  margin-bottom: 10px;

  &.mine .chat-name {
    color: #8dffb0;
  }
}

.chat-meta {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 2px;
}

.chat-name {
  color: #ffe885;
  font-size: 12px;
  font-weight: 700;
}

.chat-time {
  color: rgba(255, 244, 184, 0.45);
  font-size: 11px;
}

.chat-text {
  color: #fff;
  font-size: 14px;
  line-height: 1.4;
  word-break: break-word;
}

.emoji-panel {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 52px;
  z-index: 3;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
  max-height: 188px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid rgba(248, 239, 148, 0.45);
  border-radius: 10px;
  background: rgba(12, 28, 20, 0.98);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
}

.emoji-item {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;

  img {
    width: 32px;
    height: 32px;
    object-fit: contain;
    pointer-events: none;
  }

  &:hover,
  &:active {
    background: rgba(255, 232, 133, 0.16);
  }
}

.chat-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-top: 1px solid rgba(255, 232, 133, 0.2);
}

.chat-editor {
  flex: 1;
  min-width: 0;
  height: 36px;
  padding: 6px 10px;
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 16px;
  line-height: 24px;
  white-space: nowrap;
  outline: none;
  -webkit-overflow-scrolling: touch;

  &.is-empty::before {
    content: attr(data-placeholder);
    color: rgba(255, 255, 255, 0.4);
    pointer-events: none;
  }

  ::v-deep .chat-inline-emoji {
    width: 22px;
    height: 22px;
    margin: 0 1px;
    vertical-align: text-bottom;
    object-fit: contain;
    user-select: none;
  }
}

.emoji-btn {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid rgba(255, 232, 133, 0.45);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.35);
  color: #ffe885;
  cursor: pointer;

  svg {
    width: 22px;
    height: 22px;
    display: block;
  }

  &.active,
  &:hover {
    color: #ffd700;
    border-color: rgba(255, 215, 0, 0.85);
    background: rgba(255, 215, 0, 0.12);
  }
}

.send-btn {
  flex: none;
  height: 36px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #d4a017, #ffd700);
  color: #1a1a2e;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.chat-toggle {
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0);
  color: #ffe885;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
    display: block;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.85);
    color: #ffd700;
  }
}

.chat-pop-enter-active,
.chat-pop-leave-active {
  transition: transform 0.22s ease, opacity 0.22s ease;
}

.chat-pop-enter,
.chat-pop-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
